// Adapters that convert server.js's loose application shape into our
// strict Application type. server.js returns score as "4.8/5" string and
// `status` (not `stage`), so we normalize here.

import type { Application, ApplicationStage } from './types';

interface RawApp {
  num?: string | number;
  id?: string | number;
  date?: string;
  company?: string;
  role?: string;
  score?: string | number;
  status?: string;
  stage?: string;
  pdf?: string;
  report?: string;
  reportPath?: string;
  pdfPath?: string;
  notes?: string;
  url?: string;
}

interface RawMetrics {
  total?: number;
  statusCounts?: Record<string, number>;
  avgScore?: string | number;
  applied?: number;
  interviews?: number;
  offers?: number;
}

export interface NormalizedDataResponse {
  applications: Application[];
  stats: {
    total: number;
    evaluated: number;
    applied: number;
    interviews: number;
    offers: number;
    phone_screen: number;
    responseRate: number;
    matchRate: number;
    weeklyEvaluated: number[];
  };
}

const CANONICAL: ApplicationStage[] = ['Evaluated', 'Applied', 'Responded', 'Phone screen', 'Interview', 'Offer', 'Rejected', 'Discarded', 'SKIP'];

function normalizeStage(s: string | undefined): ApplicationStage {
  if (!s) return 'Evaluated';
  const trimmed = s.replace(/\*/g, '').trim();
  const match = CANONICAL.find((c) => c.toLowerCase() === trimmed.toLowerCase());
  return match || 'Evaluated';
}

function normalizeScore(s: string | number | undefined): number {
  if (typeof s === 'number') return s;
  if (!s) return 0;
  const m = String(s).match(/([0-9]+(?:\.[0-9]+)?)/);
  return m ? parseFloat(m[1]) : 0;
}

function normalizeApp(raw: RawApp): Application {
  const num = Number(raw.num) || Number(raw.id) || 0;
  return {
    id: String(raw.id ?? raw.num ?? ''),
    num,
    date: raw.date || '',
    company: raw.company || '',
    role: raw.role || '',
    stage: normalizeStage(raw.status || raw.stage),
    score: normalizeScore(raw.score),
    notes: raw.notes,
    report_file: raw.reportPath ? raw.reportPath.replace(/^reports\//, '') : undefined,
    pdf_file: raw.pdfPath,
    url: raw.url,
  };
}

export function adaptDataResponse(raw: { applications?: RawApp[]; metrics?: RawMetrics; stats?: RawMetrics }): NormalizedDataResponse {
  const m = raw.metrics || raw.stats || {};
  const counts = m.statusCounts || {};
  const apps = (raw.applications || []).map(normalizeApp);

  return {
    applications: apps,
    stats: {
      total: m.total || apps.length,
      evaluated: counts['Evaluated'] || 0,
      applied: counts['Applied'] || 0,
      interviews: counts['Interview'] || 0,
      offers: counts['Offer'] || 0,
      phone_screen: counts['Phone screen'] || 0,
      responseRate: 0,
      matchRate: 0,
      weeklyEvaluated: [0, 0, 0, 0, 0, 0, 0],
    },
  };
}
